// Export of any size (job R-07, step E72; docs/CRM_TOOLS.md "R-07"). "Download CSV" on Companies, People, Ask and
// Exports reads the WHOLE view, page by page, with no row cap: firms and people through crm_list_records (200 a page,
// its keyset cursor, the list's search box too), every other Ask list through crm_ask_query (1,000 a page, a keyset
// on the sort column and then id, worked out here with Ask's own operators). Each page becomes CSV text at once and
// its rows are let go, so a big view never sits in memory twice. When the file is ready, act export_view records the
// download (one export.downloaded event at E72), and only then is the file handed to the browser: every download is
// on the record.
import type { Crm } from "./crm";
import { columnLabel, visibleColumns, type AskColumn, type AskFilter, type AskQueryResponse, type AskSort, type AskSpec } from "./ask";
import { listRecords, serverSpec, type RecordCursor, type RecordSpec } from "./records";
import { downloadCsv, type CsvColumn } from "./csv";

export type ExportSource = "firms" | "people" | "activity" | "deals" | "meetings" | "events";
export const LIST_PAGE = 200;
export const ASK_PAGE = 1000;
/** A sanity bound, far above any business's CRM: a loop that never ends stops here. */
export const MAX_PAGES = 5000;

export type ExportRow = Record<string, unknown>;
type Spec = AskSpec | RecordSpec;

export interface ExportProgress {
  done: number;
  total: number;
}

export interface ExportOptions {
  /** The view as the page runs it: a list's spec (with its search box) or an Ask spec. */
  spec: Spec;
  /** The file name, e.g. datedName("companies"). */
  filename: string;
  /** CSV columns. Without them, the view's own columns (as Ask shows them), id and firm_id left out. */
  columns?: (cols: AskColumn[]) => CsvColumn<ExportRow>[];
  /** The saved view the download came from (Companies and People), named in the event. */
  view?: string | null;
  onProgress?: (p: ExportProgress) => void;
  signal?: AbortSignal;
  /** Tests: where the finished file goes (default: the browser's download). */
  save?: (filename: string, csv: string) => void;
}

export interface ExportResult {
  rows: number;
  total: number;
}

const isList = (s: string): s is "firms" | "people" => s === "firms" || s === "people";

function cell(v: unknown): string {
  if (v === null || v === undefined) return "";
  let s = typeof v === "object" ? JSON.stringify(v) : String(v);
  // Spreadsheet formula injection: a cell starting with = + - @ (or tab/CR) is shown as text (as lib/csv.ts does).
  if (/^[=+\-@\t\r]/.test(s)) s = `'${s}`;
  return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}
const line = (cells: unknown[]) => cells.map(cell).join(",") + "\r\n";

/** The view's own columns as CSV columns, with Ask's headings. */
export function defaultColumns(cols: AskColumn[]): CsvColumn<ExportRow>[] {
  return visibleColumns(cols).map((c) => ({ header: columnLabel(c.name), value: (r: ExportRow) => r[c.name] }));
}

function aborted(signal?: AbortSignal) {
  if (signal?.aborted) throw new Error("The download was stopped.");
}

/**
 * Every row of a view, one page at a time, in the view's order. `onPage` gets each page (and the columns and total);
 * nothing is kept here.
 */
export async function readView(
  crm: Crm, spec: Spec, onPage: (rows: ExportRow[], cols: AskColumn[], total: number) => void, signal?: AbortSignal,
): Promise<number> {
  const source = String(spec.source);
  if (isList(source)) {
    let cursor: RecordCursor | null = null;
    let total = 0;
    for (let n = 0; n < MAX_PAGES; n++) {
      aborted(signal);
      const page = await listRecords(crm, spec as RecordSpec, cursor, LIST_PAGE);
      total = page.total;
      onPage(page.rows, page.columns, total);
      cursor = page.next_cursor;
      if (!cursor) return total;
    }
    throw new Error("That view is too large to download in one go.");
  }
  return readAskView(crm, spec as AskSpec, onPage, signal);
}

/**
 * An Ask list other than firms and people: crm_ask_query caps a page at 1,000, so the pages are a keyset of Ask
 * filters (the rest of the rows with the same sort value and a later id, then the later values, then the empty values
 * by id), exactly as crm_list_records does in SQL.
 */
async function readAskView(
  crm: Crm, spec: AskSpec, onPage: (rows: ExportRow[], cols: AskColumn[], total: number) => void, signal?: AbortSignal,
): Promise<number> {
  const first: AskSort = spec.sort?.[0] ?? { field: "id", dir: "asc" };
  const field = first.field;
  const dir = first.dir === "desc" ? "desc" : "asc";
  const after = dir === "desc" ? "lt" : "gt";
  const base = spec.filters ?? [];
  const columns = spec.columns?.length ? (spec.columns.includes(field) ? spec.columns : [...spec.columns, field]) : undefined;
  const sort: AskSort[] = field === "id" ? [{ field: "id", dir: "asc" }] : [{ field, dir }, { field: "id", dir: "asc" }];
  const ask = (filters: AskFilter[], by: AskSort[] = sort) =>
    crm.read<AskQueryResponse | null>("crm_ask_query", { p_spec: { source: spec.source, filters, sort: by, ...(columns ? { columns } : {}), limit: ASK_PAGE } });

  const head = await ask(base);
  const total = Number(head?.total ?? 0);
  const cols = head?.columns ?? [];
  let rows = head?.rows ?? [];
  onPage(rows, cols, total);
  let read = rows.length;
  let inNulls = false;
  for (let n = 0; read < total && rows.length > 0; n++) {
    if (n >= MAX_PAGES) throw new Error("That view is too large to download in one go.");
    aborted(signal);
    const last = rows[rows.length - 1]!;
    const v = last[field];
    const id = String(last.id);
    const empty = v === null || v === undefined;
    const parts: AskFilter[][] = field === "id"
      ? [[{ field: "id", op: "gt", value: id }]]
      : inNulls || empty
        ? [[{ field, op: "is_null" }, { field: "id", op: "gt", value: id }]]
        : [
            [{ field, op: "eq", value: v }, { field: "id", op: "gt", value: id }],
            [{ field, op: after, value: v }],
            [{ field, op: "is_null" }],
          ];
    rows = [];
    for (const part of parts) {
      const r = await ask([...base, ...part], part[0]!.op === "is_null" ? [{ field: "id", dir: "asc" }] : sort);
      const got = (r?.rows ?? []).slice(0, ASK_PAGE - rows.length);
      rows.push(...got);
      if (part[0]!.op === "is_null" && got.length) inNulls = true;
      if (rows.length >= ASK_PAGE) break;
    }
    onPage(rows, cols, total);
    read += rows.length;
  }
  return total;
}

/** Reads the whole view into a CSV, records the download (E72) and hands the file to the browser. */
export async function exportView(crm: Crm, opts: ExportOptions): Promise<ExportResult> {
  const source = String(opts.spec.source) as ExportSource;
  const parts: string[] = [];
  let csvCols: CsvColumn<ExportRow>[] | null = null;
  let rows = 0;
  let total = 0;
  await readView(
    crm,
    opts.spec,
    (page, cols, t) => {
      total = t;
      if (!csvCols) {
        csvCols = opts.columns ? opts.columns(cols) : defaultColumns(cols);
        parts.push(line(csvCols.map((c) => c.header)));
      }
      const cs = csvCols;
      let chunk = "";
      for (const r of page) chunk += line(cs.map((c) => c.value(r)));
      if (chunk) parts.push(chunk);
      rows += page.length;
      opts.onProgress?.({ done: rows, total: Math.max(t, rows) });
    },
    opts.signal,
  );
  if (!csvCols) parts.push(line((opts.columns ? opts.columns([]) : []).map((c) => c.header)));
  aborted(opts.signal);
  await crm.write("export_view", {
    source,
    spec: loggedSpec(opts.spec),
    rows,
    ...(opts.view && isList(source) ? { view: opts.view } : {}),
  });
  (opts.save ?? downloadCsv)(opts.filename, parts.join(""));
  return { rows, total: Math.max(total, rows) };
}

/** What the record of a download keeps: the filters, sort, columns and (for a list) its search box. */
export function loggedSpec(spec: Spec): Record<string, unknown> {
  const source = String(spec.source);
  if (isList(source)) {
    const s = serverSpec(spec as RecordSpec);
    return { filters: s.filters, ...(s.sort ? { sort: s.sort } : {}), ...(s.columns ? { columns: s.columns } : {}), ...(s.search ? { search: s.search } : {}) };
  }
  const a = spec as AskSpec;
  return { filters: a.filters ?? [], ...(a.sort?.length ? { sort: a.sort } : {}), ...(a.columns?.length ? { columns: a.columns } : {}) };
}
